import { Controller } from "@hotwired/stimulus";

/**
 * Payment Controller - Payment method selection & submission
 *
 * Responsibilities:
 * - Payment provider selection (credit card, direct debit, etc.)
 * - Payment form loading & display for selected provider
 * - Payment form submission & response handling
 * - Payment cancellation & provider switching
 * - Loading states & error handling
 *
 * Architecture:
 * Payment forms are server-rendered HTML with provider SDKs (Braintree, Mollie).
 * These providers require direct DOM access to inject their payment widgets.
 * The controller coordinates between the provider SDKs and Rails backend.
 *
 * Pricing and plan previews are JSON-driven elsewhere; payment flows intentionally remain HTML-based.
 */

export default class extends Controller {
  static targets = ["providerSelector", "paymentContainer", "paymentForm"];
  static values = { submitUrl: String, submitMethod: String };

  connect() {
    // Braintree dispatches payment:submit event after successful payment method selection
    this.element.addEventListener("payment:submit", this.submitPlan);
  }

  disconnect() {
    this.element.removeEventListener("payment:submit", this.submitPlan);
  }

  submitPlan = async (event) => {
    if (!this.hasPaymentFormTarget) return;

    // Multiple payment controllers can be mounted on the same page (e.g. page-level new-plan
    // flow + inline billing-section editor). Without stopping propagation here, both would
    // submit the same Braintree event and one would post to the wrong endpoint.
    event.preventDefault();
    event.stopPropagation();

    const form = this.paymentFormTarget;
    const url = this.hasSubmitUrlValue ? this.submitUrlValue : form.dataset.url;
    const method = this.hasSubmitMethodValue ? this.submitMethodValue : "POST";
    const body = this.buildSubmitBody(form, method);

    this.toggleLoader(true);

    try {
      const response = await fetch(url, {
        method,
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']").content
        },
        body
      });

      if (!response.ok) {
        throw new Error(`Unexpected HTTP status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success || !data.redirect) {
        throw new Error(data.error || this.defaultErrorMessage());
      }

      window.location.href = data.redirect;
    } catch (error) {
      console.error("Payment submission error:", error);
      this.showError(error.message);
    } finally {
      this.toggleLoader(false);
    }
  };

  loadPaymentForm = async () => {
    const target = this.paymentContainerTarget || document.getElementById("new-plan-payment");
    const path = target.dataset.path;

    this.toggleLoader(true);

    const params = this.planParams();
    const url = `${path}?${params.toString()}`;

    try {
      const response = await fetch(url, {headers: {Accept: "text/html"}});

      if (!response.ok) {
        throw new Error(`Unexpected HTTP status: ${response.status}`);
      }

      const html = await response.text();

      this.hideProviderSelector();
      this.renderHTML(target, html);
      this.scrollToBottom();
    } catch (error) {
      this.showError(this.defaultErrorMessage());
    } finally {
      this.toggleLoader(false);
    }
  };

  cancelPayment = (event) => {
    event.preventDefault();

    this.resetProviderSelection();
    this.showProviderSelector();
    this.clearPaymentContainer();
    this.scrollToBottom();
  };

  startBillingEdit = (event) => {
    event.preventDefault();
    const section = event.currentTarget.closest(".section");
    section.classList.add("is-editing");
    section.querySelector(".billing-edit-container").scrollIntoView({ behavior: "smooth", block: "nearest" });
  };

  cancelBillingEdit = (event) => {
    event.preventDefault();
    event.currentTarget.closest(".section").classList.remove("is-editing");
    const switchPayment = document.getElementById("switch-payment-provider");
    if (switchPayment) switchPayment.innerHTML = "";
  };

  switchProvider = async (event) => {
    event.preventDefault();

    const link = event.currentTarget;
    const path = link.dataset.switchPath;
    const provider = link.dataset.switchProvider;
    if (!path || !provider) return;

    const target = document.getElementById("switch-payment-provider");
    if (!target) return;

    this.toggleLoader(true);

    try {
      const response = await fetch(`${path}?provider=${encodeURIComponent(provider)}`, {
        headers: { Accept: "text/html" },
        credentials: "same-origin"
      });

      if (!response.ok) throw new Error(`Unexpected HTTP status: ${response.status}`);

      const html = await response.text();
      this.renderHTML(target, html);
      target.style.display = "block";
      target.scrollIntoView({ behavior: "smooth", block: "nearest" });
    } catch (error) {
      this.showError(this.defaultErrorMessage());
    } finally {
      this.toggleLoader(false);
    }
  };

  selectProvider = async (event) => {
    event.preventDefault();

    const providerEl = event.currentTarget.closest(".payment-method-select");
    if (!providerEl || providerEl.classList.contains("active")) return;

    const provider = providerEl.dataset.provider;
    const method = providerEl.dataset.method;

    this.trackEvent("new_pricing_payment_method_reached");

    try {
      this.updateHiddenFields(provider, method);
      await this.loadPaymentForm();
      this.setActiveProvider(providerEl);
    } catch (error) {
      this.resetProviderSelection();
      this.showError(this.defaultErrorMessage());
    }
  };

  resetProviderSelection() {
    const selector = this.getProviderSelector();
    const active = selector.querySelectorAll(".payment-method-select.active");
    active.forEach(el => el.classList.remove("active"));
  }

  showError(message) {
    const modal = document.getElementById("plan-error-modal");
    if (!modal || !window.$) return;

    const body = modal.querySelector(".modal-body");
    if (body) body.textContent = message;

    window.$(modal).modal("show");
  }

  // The shared Braintree form submits nested plan[...] fields, which is what the new-plan
  // POST endpoint expects. The payment-provider PUT endpoint, however, reads top-level
  // `provider` / `payment_method_nonce` / `payment_device_data` (see edit.html.erb and
  // Admin::PlanPaymentProvidersController#update). Mirror them up for the update flow so we
  // match the existing controller contract without forking the form.
  buildSubmitBody(form, method) {
    const formData = new FormData(form);
    if (method.toUpperCase() !== "PUT") return formData;

    const liftField = (nestedKey, flatKey) => {
      const value = formData.get(nestedKey);
      if (value !== null && !formData.has(flatKey)) formData.append(flatKey, value);
    };

    liftField("plan[payment_provider]", "provider");
    liftField("plan[payment_method_nonce]", "payment_method_nonce");
    liftField("plan[payment_device_data]", "payment_device_data");

    return formData;
  }

  planParams() {
    const params = new URLSearchParams();
    const skipFields = ["plan[payment_method_nonce]", "plan[payment_device_data]"];

    // When this controller is mounted at the page level (#plans), the element also contains
    // the billing-section's hidden plan[...] inputs (current plan, empty payment_provider) in
    // addition to the in-progress new-plan form. Sweeping both produces duplicate URL params
    // (the empty billing-section values win, since they come later in DOM order) which strong
    // params then rejects as missing. Scoping to #new-plan when it lives inside this.element
    // means we only see the form the user is actually filling in. When mounted on .payment-edit
    // (the inline payment-method update flow), #new-plan isn't a descendant so we fall back
    // and use this.element as before.
    const root = this.element.querySelector("#new-plan") || this.element;

    root.querySelectorAll("input[name^='plan']").forEach(input => {
      if (input.type === "checkbox" && !input.checked) return;
      if (skipFields.includes(input.name)) return;
      params.append(input.name, input.value);
    });

    return params;
  }

  toggleLoader(show) {
    const loader = document.querySelector(".loader");
    if (!loader) return;

    loader.style.display = show ? "block" : "none";
  }

  hideProviderSelector() {
    const selector = this.getProviderSelector();
    if (!selector) return;

    selector.style.display = "none";
  }

  showProviderSelector() {
    const selector = this.getProviderSelector();
    if (!selector) return;

    selector.style.display = "";
  }

  clearPaymentContainer() {
    const target = this.paymentContainerTarget || document.getElementById("new-plan-payment");
    if (target) target.innerHTML = "";
  }

  setActiveProvider(providerEl) {
    this.resetProviderSelection();
    providerEl.classList.add("active");
  }

  updateHiddenFields(provider, method) {
    const providerField = document.getElementById("plan_payment_provider");
    if (providerField) providerField.value = provider;

    const methodField = document.getElementById("plan_payment_method");
    if (methodField) methodField.value = method;
  }

  renderHTML(target, html) {
    if (!target) return;

    target.innerHTML = html;
    this.executeScripts(target);
  }

  scrollToBottom() {
    window.scrollTo({top: document.body.scrollHeight, behavior: "smooth"});
  }

  defaultErrorMessage() {
    return window.I18n?.t?.("js.error_has_occurred");
  }

  getProviderSelector() {
    return this.providerSelectorTarget || document.getElementById("select-payment-provider");
  }

  executeScripts(target) {
    // When injecting HTML, scripts do not execute automatically.
    // This function re-inserts the scripts to trigger execution.
    const scripts = target.querySelectorAll("script");

    scripts.forEach(oldScript => {
      const newScript = document.createElement("script");

      Array.from(oldScript.attributes).forEach(attr => {
        newScript.setAttribute(attr.name, attr.value);
      });
      newScript.textContent = oldScript.textContent;

      oldScript.parentNode.replaceChild(newScript, oldScript);
    });
  }

  trackEvent(eventType) {
    fetch("/admin/app_events", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name=\"csrf-token\"]").content
      },
      body: JSON.stringify({ event_type: eventType })
    }).catch(error => console.error("Tracking error:", error));
  }
}
